//TODO : Refactor after ai
/**
 * Mengenübersicht einer Sammlung.
 *
 * Dieselben Datensätze wie in der Liste, nur nicht einzeln, sondern gestapelt:
 * je Ebene eine Zeile mit den Messwerten dahinter, aufklappbar bis zur
 * feinsten Ebene. Weil sie auf denselben gefilterten Bestand schaut wie jede
 * andere Ansicht, kann sie nie etwas anderes behaupten als die Liste.
 *
 * Ist eine Zeitachse angegeben, kommt rechts je Zeitpunkt eine Spalte dazu:
 * links steht dann, **wo** etwas liegt, rechts, **wann** es fällig wird —
 * beides in einem Blick, so wie es heute im Fristenblatt nebeneinander steht.
 */

import { useMemo, useState } from "react";
import type { HTMLAttributes } from "react";
import type { ObjectAttribute, OverviewObject, OverviewRollup } from "./types";

interface Knoten {
  pfad: string;
  label: string;
  tiefe: number;
  items: OverviewObject[];
  kinder: Knoten[];
}

const OHNE = "Ohne Angabe";

const baue = (
  items: OverviewObject[],
  ebenen: OverviewRollup["ebenen"],
  tiefe: number,
  pfad: string
): Knoten[] => {
  const ebene = ebenen[tiefe];
  if (!ebene) return [];
  const eimer = new Map<string, OverviewObject[]>();
  items.forEach((item) => {
    const wert = item[ebene.key];
    const label = wert == null || wert === "" ? OHNE : String(wert);
    eimer.set(label, [...(eimer.get(label) ?? []), item]);
  });
  return [...eimer.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], "de"))
    .map(([label, gruppe]) => ({
      pfad: `${pfad}/${label}`,
      label,
      tiefe,
      items: gruppe,
      kinder: baue(gruppe, ebenen, tiefe + 1, `${pfad}/${label}`),
    }));
};

export interface ObjectSummaryProps extends HTMLAttributes<HTMLDivElement> {
  items?: OverviewObject[];
  rollup: OverviewRollup;
}

export function ObjectSummary({
  items = [],
  rollup,
  className = "",
  ...rest
}: ObjectSummaryProps) {
  const baum = useMemo(() => baue(items, rollup.ebenen, 0, ""), [items, rollup]);
  /** Die oberste Ebene steht offen — darunter klappt man auf, was man braucht. */
  const [offen, setOffen] = useState<Set<string>>(
    () => new Set(baum.map((k) => k.pfad))
  );

  const spalten: ObjectAttribute[] = rollup.messwerte(items);

  /** Die Zeitpunkte, die im gefilterten Bestand vorkommen — für alle Zeilen gleich. */
  const zeitpunkte = useMemo(() => {
    const achse = rollup.zeitachse;
    if (!achse) return [];
    const gesehen = new Map<string, string>();
    items.forEach((item) => {
      const sortierung = String(item[achse.sortKey] ?? "");
      const label = String(item[achse.key] ?? "");
      if (sortierung && !gesehen.has(sortierung)) gesehen.set(sortierung, label);
    });
    return [...gesehen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items, rollup]);
  const umschalten = (pfad: string) =>
    setOffen((alt) => {
      const neu = new Set(alt);
      if (neu.has(pfad)) neu.delete(pfad);
      else neu.add(pfad);
      return neu;
    });

  const zeilen: Knoten[] = [];
  const sammle = (knoten: Knoten[]) =>
    knoten.forEach((k) => {
      zeilen.push(k);
      if (offen.has(k.pfad)) sammle(k.kinder);
    });
  sammle(baum);

  return (
    <div className={`bui-ov-surface ${className}`} {...rest}>
      {/* Viele Monatsspalten passen nicht auf jeden Bildschirm — die
          Ebenenspalte bleibt beim Schieben stehen. */}
      <div style={{ overflowX: "auto" }}>
        <table className="bui-table">
        <thead>
          <tr>
            <th
              style={{
                minWidth: 280,
                position: "sticky",
                left: 0,
                zIndex: 1,
                background: "var(--neutral-50)",
              }}
            >
              {rollup.ebenen.map((e) => e.label).join(" › ")}
            </th>
            {spalten.map((s) => (
              <th key={s.label}>{s.label}</th>
            ))}
            {zeitpunkte.map(([sortierung, label]) => (
              <th key={sortierung} style={{ textAlign: "right" }}>
                {label}
              </th>
            ))}
            {zeitpunkte.length > 0 && (
              <th style={{ textAlign: "right" }}>{rollup.zeitachse?.titel}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((k) => {
            const werte = rollup.messwerte(k.items);
            const aufklappbar = k.kinder.length > 0;
            const istOffen = offen.has(k.pfad);
            return (
              <tr key={k.pfad}>
                <td
                  style={{
                    paddingLeft: 20 + k.tiefe * 24,
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    background: "var(--bg-primary)",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    {aufklappbar ? (
                      <button
                        type="button"
                        aria-expanded={istOffen}
                        aria-label={istOffen ? `${k.label} zuklappen` : `${k.label} aufklappen`}
                        onClick={() => umschalten(k.pfad)}
                        style={{
                          width: 20,
                          height: 20,
                          display: "grid",
                          placeItems: "center",
                          border: 0,
                          borderRadius: "var(--r-xs)",
                          background: "transparent",
                          color: "var(--icon-tertiary)",
                          cursor: "pointer",
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{
                            transform: istOffen ? "rotate(90deg)" : undefined,
                            transition: "transform var(--dur-base) var(--ease-standard)",
                          }}
                        >
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    ) : (
                      <span style={{ width: 20 }} />
                    )}
                    <span
                      style={{
                        font: "var(--t-paragraph-s)",
                        fontWeight: k.tiefe === 0 ? 600 : 400,
                        color: "var(--text-primary)",
                      }}
                    >
                      {k.label}
                    </span>
                  </span>
                </td>
                {werte.map((w) => (
                  <td key={w.label}>{w.value}</td>
                ))}
                {zeitpunkte.map(([sortierung]) => {
                  const achse = rollup.zeitachse;
                  if (!achse) return null;
                  const treffer = k.items.filter(
                    (i) => String(i[achse.sortKey] ?? "") === sortierung
                  );
                  const offen = treffer.length > 0 && achse.offen?.(treffer) === true;
                  return (
                    <td
                      key={sortierung}
                      style={{
                        textAlign: "right",
                        color: offen ? "var(--text-error)" : undefined,
                        fontWeight: offen ? 600 : undefined,
                      }}
                    >
                      {treffer.length > 0 ? achse.wert(treffer) : (
                        <span style={{ color: "var(--text-disabled)" }}>·</span>
                      )}
                    </td>
                  );
                })}
                {zeitpunkte.length > 0 && rollup.zeitachse && (
                  <td style={{ textAlign: "right", fontWeight: 600 }}>
                    {rollup.zeitachse.wert(k.items)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>
    </div>
  );
}
